import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, Plus, Trash2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../ui/select";
import TemplateMessage from "../TemplateMessage";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Criar um modelo e mandar para aprovação da Meta.
//
// A Meta é exigente e o motivo da recusa costuma ser vago, então a tela
// antecipa o que ela cobra: nome sem espaço nem acento, categoria coerente com
// o texto, e variáveis numeradas em sequência.

const CATEGORIAS = [
  {
    valor: "UTILITY",
    rotulo: "Utilitário",
    dica: "Confirmação, atualização de pedido, lembrete. Aprova mais fácil e custa menos."
  },
  {
    valor: "MARKETING",
    rotulo: "Marketing",
    dica: "Promoção, novidade, convite. Análise mais rigorosa e cobrança maior."
  },
  {
    valor: "AUTHENTICATION",
    rotulo: "Autenticação",
    dica: "Código de verificação. Formato fixo exigido pela Meta."
  }
];

const IDIOMAS = [
  { valor: "pt_BR", rotulo: "Português (Brasil)" },
  { valor: "en_US", rotulo: "Inglês (EUA)" },
  { valor: "es_ES", rotulo: "Espanhol" }
];

// O nome na Meta é minúsculo, sem espaço e sem acento. Normalizar enquanto a
// pessoa digita evita a recusa por um detalhe que ela não tinha como saber.
const normalizarNome = (texto) =>
  String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+/, "");

const ModeloWhatsappModal = ({ open, onClose, onCriado }) => {
  const [nome, setNome] = useState("");
  const [categoria, setCategoria] = useState("UTILITY");
  const [idioma, setIdioma] = useState("pt_BR");
  const [cabecalho, setCabecalho] = useState("");
  const [corpo, setCorpo] = useState("");
  const [rodape, setRodape] = useState("");
  const [botoes, setBotoes] = useState([]);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!open) return;

    setNome("");
    setCategoria("UTILITY");
    setIdioma("pt_BR");
    setCabecalho("");
    setCorpo("");
    setRodape("");
    setBotoes([]);
  }, [open]);

  const escolhida = CATEGORIAS.find((c) => c.valor === categoria);

  const previa = {
    tipo: "whatsapp",
    nome: normalizarNome(nome) || "sem_nome",
    categoria,
    cabecalho: cabecalho.trim()
      ? { tipo: "text", texto: cabecalho }
      : null,
    corpo: corpo || "(escreva o texto do modelo)",
    rodape: rodape.trim() || null,
    botoes: botoes
      .filter((b) => b.text?.trim())
      .map((b) => ({
        tipo: String(b.type || "QUICK_REPLY").toLowerCase(),
        texto: b.text,
        url: b.url,
        telefone: b.phone
      }))
  };

  const salvar = async () => {
    if (!normalizarNome(nome)) {
      toast.error("Dê um nome ao modelo.");
      return;
    }

    if (!corpo.trim()) {
      toast.error("Escreva o texto do modelo.");
      return;
    }

    setSalvando(true);

    try {
      await api.post("/meta/templates", {
        name: normalizarNome(nome),
        language: idioma,
        category: categoria,
        header: cabecalho,
        body: corpo,
        footer: rodape,
        buttons: botoes
      });

      toast.success(
        "Modelo enviado para aprovação. A Meta costuma responder em alguns minutos."
      );

      await onCriado?.();
      onClose();
    } catch (err) {
      toastError(err);
    }

    setSalvando(false);
  };

  return (
    <Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle>Criar modelo</DialogTitle>
          <DialogDescription>
            O modelo vai para a Meta aprovar. Enquanto ela não responder, ele
            não pode ser enviado.
          </DialogDescription>
        </DialogHeader>

        <div className="grid flex-1 gap-4 overflow-y-auto sm:grid-cols-2">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="modelo-nome">Nome</Label>
              <Input
                id="modelo-nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="confirmacao_de_pedido"
              />
              <p className="text-xs text-muted-foreground">
                A Meta guarda como{" "}
                <span className="font-mono">
                  {normalizarNome(nome) || "sem_nome"}
                </span>{" "}
                — minúsculo, sem espaço nem acento.
              </p>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Categoria</Label>
                <Select value={categoria} onValueChange={setCategoria}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS.map((item) => (
                      <SelectItem key={item.valor} value={item.valor}>
                        {item.rotulo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Idioma</Label>
                <Select value={idioma} onValueChange={setIdioma}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {IDIOMAS.map((item) => (
                      <SelectItem key={item.valor} value={item.valor}>
                        {item.rotulo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {escolhida && (
              <p className="text-xs text-muted-foreground">{escolhida.dica}</p>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="modelo-cabecalho">Cabeçalho (opcional)</Label>
              <Input
                id="modelo-cabecalho"
                value={cabecalho}
                onChange={(e) => setCabecalho(e.target.value)}
                placeholder="Seu pedido foi confirmado"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="modelo-corpo">Texto</Label>
              <Textarea
                id="modelo-corpo"
                rows={5}
                value={corpo}
                onChange={(e) => setCorpo(e.target.value)}
                placeholder="Olá {{1}}, seu pedido {{2}} saiu para entrega."
              />
              <p className="text-xs text-muted-foreground">
                {
                  "Use {{1}}, {{2}}… onde o texto muda por pessoa. Numere em sequência, sem pular."
                }
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="modelo-rodape">Rodapé (opcional)</Label>
              <Input
                id="modelo-rodape"
                value={rodape}
                onChange={(e) => setRodape(e.target.value)}
                placeholder="Responda SAIR para não receber mais"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Botões (opcional)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={botoes.length >= 3}
                  onClick={() =>
                    setBotoes((atuais) => [
                      ...atuais,
                      { type: "QUICK_REPLY", text: "" }
                    ])
                  }
                >
                  <Plus className="h-3.5 w-3.5" />
                  Botão
                </Button>
              </div>

              {botoes.map((botao, indice) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={indice} className="flex items-center gap-1.5">
                  <Select
                    value={botao.type}
                    onValueChange={(valor) =>
                      setBotoes((atuais) =>
                        atuais.map((b, i) =>
                          i === indice ? { ...b, type: valor } : b
                        )
                      )
                    }
                  >
                    <SelectTrigger className="w-32 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="QUICK_REPLY">Resposta</SelectItem>
                      <SelectItem value="URL">Link</SelectItem>
                      <SelectItem value="PHONE_NUMBER">Telefone</SelectItem>
                    </SelectContent>
                  </Select>

                  <Input
                    className="flex-1"
                    placeholder="Texto do botão"
                    value={botao.text}
                    onChange={(e) =>
                      setBotoes((atuais) =>
                        atuais.map((b, i) =>
                          i === indice ? { ...b, text: e.target.value } : b
                        )
                      )
                    }
                  />

                  {botao.type === "URL" && (
                    <Input
                      className="flex-1"
                      placeholder="https://…"
                      value={botao.url || ""}
                      onChange={(e) =>
                        setBotoes((atuais) =>
                          atuais.map((b, i) =>
                            i === indice ? { ...b, url: e.target.value } : b
                          )
                        )
                      }
                    />
                  )}

                  {botao.type === "PHONE_NUMBER" && (
                    <Input
                      className="flex-1"
                      placeholder="+55…"
                      value={botao.phone || ""}
                      onChange={(e) =>
                        setBotoes((atuais) =>
                          atuais.map((b, i) =>
                            i === indice ? { ...b, phone: e.target.value } : b
                          )
                        )
                      }
                    />
                  )}

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setBotoes((atuais) =>
                        atuais.filter((_, i) => i !== indice)
                      )
                    }
                    aria-label="Remover botão"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">O cliente vai ver</Label>
            <div className="mt-1.5">
              <TemplateMessage dados={previa} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button onClick={salvar} disabled={salvando}>
            {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
            Mandar para aprovação
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModeloWhatsappModal;
